import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash2, Eye, ClipboardCheck, Calendar, CalendarClock, Users, Wrench, Camera,
  Home, Sun, Fuel, Bird, Shell, FileText, Save, X, Printer, ArrowLeftRight,
  ArrowUp, ArrowDown, Minus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MediaUploader, MediaViewer } from '@/components/MediaUploader';
import { exportElementToPDF } from '@/utils/pdfExport';
import { formatDate } from '@/utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const PENS = ['Pen 1', 'Pen 2', 'Pen 3', 'Pen 4', 'Pen 5', 'Pen 6', 'Brooding Pen', 'Grower Pen'];
const RATINGS = ['Excellent', 'Good', 'Fair', 'Poor', 'Critical'];
const YESNO = ['Yes', 'No'];

const emptyReport = (name) => ({
  audit_date: new Date().toISOString().split('T')[0],
  next_audit_date: '',
  auditor_name: name || '',
  staffing: { total_staff: '', staff_present: '', staff_rating: 'Good', notes: '', attachments: [] },
  staffing_areas: [{ area: 'Office / Admin', condition: 'Good', notes: '', attachments: [] }],
  equipment: [{ name: '', type: '', state: 'Good', working: 'Yes', notes: '', attachments: [] }],
  cameras: { total: '', working: '', state: 'Good', notes: '', attachments: [] },
  pens: PENS.map(p => ({ pen: p, cage_condition: 'Good', general_condition: 'Good', notes: '', attachments: [] })),
  snail_house: { condition: 'Good', humidity: '', cleanliness: 'Good', notes: '', attachments: [] },
  power_solar: { available: 'Yes', state: 'Good', notes: '', attachments: [] },
  power_generator: { available: 'Yes', state: 'Good', fuel_level: '', notes: '', attachments: [] },
  chicken_census: PENS.map(p => ({ location: p, count: '', notes: '' })),
  snail_census: [{ location: 'Snail Green House', count: '', notes: '' }],
  general_conditions: '',
  recommendations: '',
  attestation_name: '',
  status: 'submitted',
  attachments: []
});

const Section = ({ icon: Icon, title, children, desc }) => (
  <div className="border border-slate-200 rounded-xl p-4 bg-white">
    <div className="flex items-center gap-2 mb-3">
      <span className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"><Icon className="w-4 h-4" /></span>
      <div>
        <h3 className="font-semibold text-slate-800 text-sm">{title}</h3>
        {desc && <p className="text-xs text-slate-400">{desc}</p>}
      </div>
    </div>
    {children}
  </div>
);

const RatingSelect = ({ value, onChange, options = RATINGS, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

const AuditorReport = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = user?.role?.toLowerCase() || '';
  const isAuditor = role === 'auditor';
  const canManage = role === 'admin' || role === 'manager';

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(emptyReport(user?.name));
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  const viewRef = useRef(null);

  const autosaveKey = (id) => `audit_autosave_${user?.id || 'anon'}_${id || 'new'}`;

  useEffect(() => { fetchReports(); /* eslint-disable-next-line */ }, []);

  // Quietly auto-save the open form to localStorage so nothing is lost if the phone closes
  useEffect(() => {
    if (!formOpen) return;
    const key = autosaveKey(editingId);
    const t = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify(form));
        setAutoSavedAt(new Date());
      } catch (e) { /* storage full/unavailable */ }
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [form, formOpen, editingId]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auditor-reports`, getAuthHeader());
      setReports(res.data);
    } catch (e) {
      toast.error('Failed to load auditor reports');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditingId(null);
    setAutoSavedAt(null);
    const key = autosaveKey(null);
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setForm({ ...emptyReport(user?.name), ...parsed });
        toast.success('Restored your unsaved draft', {
          action: {
            label: 'Discard',
            onClick: () => { localStorage.removeItem(key); setForm(emptyReport(user?.name)); }
          }
        });
      } catch (e) {
        setForm(emptyReport(user?.name));
      }
    } else {
      setForm(emptyReport(user?.name));
    }
    setFormOpen(true);
  };
  const openEdit = (r) => { setEditingId(r.id); setAutoSavedAt(null); setForm({ ...emptyReport(user?.name), ...r }); setFormOpen(true); };

  const set = (path, value) => {
    setForm(prev => {
      const next = { ...prev };
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) { obj[keys[i]] = { ...obj[keys[i]] }; obj = obj[keys[i]]; }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };
  const setListItem = (listKey, idx, field, value) => {
    setForm(prev => {
      const list = [...prev[listKey]];
      list[idx] = { ...list[idx], [field]: value };
      return { ...prev, [listKey]: list };
    });
  };
  const addListItem = (listKey, item) => setForm(prev => ({ ...prev, [listKey]: [...prev[listKey], item] }));
  const removeListItem = (listKey, idx) => setForm(prev => ({ ...prev, [listKey]: prev[listKey].filter((_, i) => i !== idx) }));

  const handleSave = async (status) => {
    if (!form.audit_date || !form.auditor_name.trim()) {
      toast.error('Audit date and auditor name are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, status };
      if (editingId) {
        await axios.put(`${API}/auditor-reports/${editingId}`, payload, getAuthHeader());
      } else {
        await axios.post(`${API}/auditor-reports`, payload, getAuthHeader());
      }
      toast.success(status === 'draft'
        ? 'Draft saved — you can resume it anytime'
        : (editingId ? 'Report submitted to management' : 'Report submitted to management'));
      try { localStorage.removeItem(autosaveKey(editingId)); } catch (e) { /* ignore */ }
      setAutoSavedAt(null);
      setFormOpen(false);
      fetchReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this audit report?')) return;
    try {
      await axios.delete(`${API}/auditor-reports/${id}`, getAuthHeader());
      toast.success('Report deleted');
      fetchReports();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const downloadPDF = async () => {
    if (!viewRef.current || !viewing) return;
    setPdfBusy(true);
    try {
      await exportElementToPDF(viewRef.current, `audit-report-${viewing.auditor_name}-${viewing.audit_date}.pdf`);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('Failed to generate PDF');
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="auditor-report-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Auditor's Report</h1>
          <p className="text-slate-600 mt-1">
            {canManage ? 'Farm audit reports submitted by the appointed Auditor' : 'Complete a farm audit checklist and submit to management'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {reports.length >= 2 && (
            <Button variant="outline" onClick={() => setCompareOpen(true)} className="gap-2" data-testid="compare-audits-btn">
              <ArrowLeftRight className="w-4 h-4" /> Compare Last Two
            </Button>
          )}
          <Button onClick={openNew} className="gap-2 bg-indigo-600 hover:bg-indigo-700" data-testid="new-audit-report-btn">
            <Plus className="w-4 h-4" /> New Audit Report
          </Button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
        </div>
      ) : reports.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600">No audit reports yet</h3>
          <p className="text-slate-400 mt-1">Create your first farm audit report</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="audit-reports-list">
          {reports.map(r => (
            <div key={r.id} className="bg-white rounded-xl border shadow-sm p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 text-slate-800 font-semibold">
                    <ClipboardCheck className="w-4 h-4 text-indigo-600" /> {r.auditor_name}
                    {r.status === 'draft' && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium" data-testid={`draft-badge-${r.id}`}>Draft</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatDate(r.audit_date)}</span>
                    {r.next_audit_date && <span className="flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Next: {formatDate(r.next_audit_date)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => { setViewing(r); setViewOpen(true); }} data-testid={`view-audit-${r.id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  {(isAuditor && r.created_by === user?.id) || canManage ? (
                    <Button variant="ghost" size="icon" onClick={() => openEdit(r)} title="Edit"><FileText className="w-4 h-4" /></Button>
                  ) : null}
                  {((isAuditor && r.created_by === user?.id) || canManage) && (
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(r.id)} className="text-red-500" data-testid={`delete-audit-${r.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              {r.recommendations && <p className="text-sm text-slate-600 mt-3 line-clamp-2"><span className="font-medium">Recommendations:</span> {r.recommendations}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Form */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit' : 'New'} Farm Audit Report</DialogTitle>
            <DialogDescription>Complete the farm audit checklist and submit it to management.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Meta */}
            <Section icon={Calendar} title="Audit Details">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Audit Date *</Label><Input type="date" value={form.audit_date} onChange={e => set('audit_date', e.target.value)} data-testid="audit-date-input" /></div>
                <div><Label className="text-xs">Proposed Next Audit Date</Label><Input type="date" value={form.next_audit_date} onChange={e => set('next_audit_date', e.target.value)} data-testid="next-audit-date-input" /></div>
                <div><Label className="text-xs">Auditor Name *</Label><Input value={form.auditor_name} onChange={e => set('auditor_name', e.target.value)} data-testid="auditor-name-input" /></div>
              </div>
            </Section>

            {/* Staffing */}
            <Section icon={Users} title="Staffing & Staffing Areas" desc="Personnel and the state of work areas">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label className="text-xs">Total Staff</Label><Input type="number" value={form.staffing.total_staff} onChange={e => set('staffing.total_staff', e.target.value)} /></div>
                <div><Label className="text-xs">Present on Audit</Label><Input type="number" value={form.staffing.staff_present} onChange={e => set('staffing.staff_present', e.target.value)} /></div>
                <div><Label className="text-xs">Overall Rating</Label><RatingSelect value={form.staffing.staff_rating} onChange={v => set('staffing.staff_rating', v)} /></div>
              </div>
              <Textarea className="mt-2" placeholder="Notes on staffing / conduct / attendance" value={form.staffing.notes} onChange={e => set('staffing.notes', e.target.value)} />
              <div className="mt-2"><MediaUploader compact label="Staffing photos / videos" attachments={form.staffing.attachments} setAttachments={v => set('staffing.attachments', v)} /></div>

              <div className="mt-4 space-y-2">
                <Label className="text-xs font-semibold">Staffing Areas</Label>
                {form.staffing_areas.map((a, i) => (
                  <div key={i} className="border rounded-lg p-3 space-y-2 bg-slate-50">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <Input placeholder="Area (e.g. Store, Office)" value={a.area} onChange={e => setListItem('staffing_areas', i, 'area', e.target.value)} />
                      <RatingSelect value={a.condition} onChange={v => setListItem('staffing_areas', i, 'condition', v)} />
                      <div className="flex gap-2">
                        <Input placeholder="Notes" value={a.notes} onChange={e => setListItem('staffing_areas', i, 'notes', e.target.value)} />
                        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => removeListItem('staffing_areas', i)}><X className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    <MediaUploader compact label="" attachments={a.attachments || []} setAttachments={v => setListItem('staffing_areas', i, 'attachments', v)} />
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addListItem('staffing_areas', { area: '', condition: 'Good', notes: '', attachments: [] })} className="gap-1"><Plus className="w-3 h-3" /> Add Area</Button>
              </div>
            </Section>

            {/* Equipment */}
            <Section icon={Wrench} title="Farm Equipment & Tools" desc="Type, state and functionality of each item">
              <div className="space-y-2">
                {form.equipment.map((eq, i) => (
                  <div key={i} className="border rounded-lg p-3 space-y-2 bg-slate-50">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <Input placeholder="Equipment name" value={eq.name} onChange={e => setListItem('equipment', i, 'name', e.target.value)} />
                      <Input placeholder="Type / model" value={eq.type} onChange={e => setListItem('equipment', i, 'type', e.target.value)} />
                      <RatingSelect value={eq.state} onChange={v => setListItem('equipment', i, 'state', v)} />
                      <div className="flex gap-2 items-center">
                        <RatingSelect value={eq.working} onChange={v => setListItem('equipment', i, 'working', v)} options={YESNO} />
                        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => removeListItem('equipment', i)}><X className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    <Input placeholder="Notes" value={eq.notes} onChange={e => setListItem('equipment', i, 'notes', e.target.value)} />
                    <MediaUploader compact label="" attachments={eq.attachments || []} setAttachments={v => setListItem('equipment', i, 'attachments', v)} />
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addListItem('equipment', { name: '', type: '', state: 'Good', working: 'Yes', notes: '', attachments: [] })} className="gap-1"><Plus className="w-3 h-3" /> Add Equipment</Button>
              </div>
              <div className="mt-4 border rounded-lg p-3 bg-slate-50">
                <Label className="text-xs font-semibold flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Cameras (CCTV)</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  <div><Label className="text-xs">Total</Label><Input type="number" value={form.cameras.total} onChange={e => set('cameras.total', e.target.value)} /></div>
                  <div><Label className="text-xs">Working</Label><Input type="number" value={form.cameras.working} onChange={e => set('cameras.working', e.target.value)} /></div>
                  <div><Label className="text-xs">State</Label><RatingSelect value={form.cameras.state} onChange={v => set('cameras.state', v)} /></div>
                </div>
                <Input className="mt-2" placeholder="Camera notes" value={form.cameras.notes} onChange={e => set('cameras.notes', e.target.value)} />
                <div className="mt-2"><MediaUploader compact label="" attachments={form.cameras.attachments} setAttachments={v => set('cameras.attachments', v)} /></div>
              </div>
            </Section>

            {/* Pens / Cages */}
            <Section icon={Home} title="Cages & Pen Conditions" desc="General condition of each pen and its cages">
              <div className="space-y-2">
                {form.pens.map((p, i) => (
                  <div key={i} className="border rounded-lg p-3 space-y-2 bg-slate-50">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-center">
                      <span className="text-sm font-medium text-slate-700">{p.pen}</span>
                      <div><Label className="text-xs">Cage</Label><RatingSelect value={p.cage_condition} onChange={v => setListItem('pens', i, 'cage_condition', v)} /></div>
                      <div><Label className="text-xs">General</Label><RatingSelect value={p.general_condition} onChange={v => setListItem('pens', i, 'general_condition', v)} /></div>
                      <Input placeholder="Notes" value={p.notes} onChange={e => setListItem('pens', i, 'notes', e.target.value)} />
                    </div>
                    <MediaUploader compact label="" attachments={p.attachments || []} setAttachments={v => setListItem('pens', i, 'attachments', v)} />
                  </div>
                ))}
              </div>
            </Section>

            {/* Snail Green House */}
            <Section icon={Shell} title="Snail Green House" desc="Condition of the snailery">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div><Label className="text-xs">Condition</Label><RatingSelect value={form.snail_house.condition} onChange={v => set('snail_house.condition', v)} /></div>
                <div><Label className="text-xs">Humidity</Label><Input placeholder="e.g. Adequate" value={form.snail_house.humidity} onChange={e => set('snail_house.humidity', e.target.value)} /></div>
                <div><Label className="text-xs">Cleanliness</Label><RatingSelect value={form.snail_house.cleanliness} onChange={v => set('snail_house.cleanliness', v)} /></div>
              </div>
              <Textarea className="mt-2" placeholder="Snail house notes" value={form.snail_house.notes} onChange={e => set('snail_house.notes', e.target.value)} />
              <div className="mt-2"><MediaUploader compact label="" attachments={form.snail_house.attachments} setAttachments={v => set('snail_house.attachments', v)} /></div>
            </Section>

            {/* Power Systems */}
            <Section icon={Sun} title="Power Generating Systems">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="border rounded-lg p-3 bg-slate-50">
                  <Label className="text-xs font-semibold flex items-center gap-1"><Sun className="w-3.5 h-3.5" /> Solar Power System</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div><Label className="text-xs">Available</Label><RatingSelect value={form.power_solar.available} onChange={v => set('power_solar.available', v)} options={YESNO} /></div>
                    <div><Label className="text-xs">State</Label><RatingSelect value={form.power_solar.state} onChange={v => set('power_solar.state', v)} /></div>
                  </div>
                  <Input className="mt-2" placeholder="Notes" value={form.power_solar.notes} onChange={e => set('power_solar.notes', e.target.value)} />
                  <div className="mt-2"><MediaUploader compact label="" attachments={form.power_solar.attachments} setAttachments={v => set('power_solar.attachments', v)} /></div>
                </div>
                <div className="border rounded-lg p-3 bg-slate-50">
                  <Label className="text-xs font-semibold flex items-center gap-1"><Fuel className="w-3.5 h-3.5" /> Petrol Generator</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div><Label className="text-xs">Available</Label><RatingSelect value={form.power_generator.available} onChange={v => set('power_generator.available', v)} options={YESNO} /></div>
                    <div><Label className="text-xs">State</Label><RatingSelect value={form.power_generator.state} onChange={v => set('power_generator.state', v)} /></div>
                    <div><Label className="text-xs">Fuel</Label><Input placeholder="Level" value={form.power_generator.fuel_level} onChange={e => set('power_generator.fuel_level', e.target.value)} /></div>
                  </div>
                  <Input className="mt-2" placeholder="Notes" value={form.power_generator.notes} onChange={e => set('power_generator.notes', e.target.value)} />
                  <div className="mt-2"><MediaUploader compact label="" attachments={form.power_generator.attachments} setAttachments={v => set('power_generator.attachments', v)} /></div>
                </div>
              </div>
            </Section>

            {/* Livestock Census */}
            <Section icon={Bird} title="Livestock Census" desc="Head-count of chickens and snails during the audit">
              <Label className="text-xs font-semibold">Chicken Census</Label>
              <div className="space-y-2 mt-1">
                {form.chicken_census.map((c, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
                    <Input placeholder="Location" value={c.location} onChange={e => setListItem('chicken_census', i, 'location', e.target.value)} />
                    <Input type="number" placeholder="Count" value={c.count} onChange={e => setListItem('chicken_census', i, 'count', e.target.value)} />
                    <div className="flex gap-2">
                      <Input placeholder="Notes" value={c.notes} onChange={e => setListItem('chicken_census', i, 'notes', e.target.value)} />
                      <Button variant="ghost" size="icon" className="text-red-500" onClick={() => removeListItem('chicken_census', i)}><X className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addListItem('chicken_census', { location: '', count: '', notes: '' })} className="gap-1"><Plus className="w-3 h-3" /> Add Row</Button>
              </div>
              <Label className="text-xs font-semibold mt-4 block">Snail Census</Label>
              <div className="space-y-2 mt-1">
                {form.snail_census.map((c, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
                    <Input placeholder="Location" value={c.location} onChange={e => setListItem('snail_census', i, 'location', e.target.value)} />
                    <Input type="number" placeholder="Count" value={c.count} onChange={e => setListItem('snail_census', i, 'count', e.target.value)} />
                    <div className="flex gap-2">
                      <Input placeholder="Notes" value={c.notes} onChange={e => setListItem('snail_census', i, 'notes', e.target.value)} />
                      <Button variant="ghost" size="icon" className="text-red-500" onClick={() => removeListItem('snail_census', i)}><X className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addListItem('snail_census', { location: '', count: '', notes: '' })} className="gap-1"><Plus className="w-3 h-3" /> Add Row</Button>
              </div>
            </Section>

            {/* Overall */}
            <Section icon={FileText} title="General Conditions & Recommendations">
              <Label className="text-xs">General Conditions Observed</Label>
              <Textarea value={form.general_conditions} onChange={e => set('general_conditions', e.target.value)} placeholder="Overall summary of the farm's condition" />
              <Label className="text-xs mt-3 block">Recommendations for Improvement</Label>
              <Textarea value={form.recommendations} onChange={e => set('recommendations', e.target.value)} placeholder="Your recommendations to management" data-testid="recommendations-input" />
              <div className="mt-3"><Label className="text-xs">Additional Photos / Videos</Label><MediaUploader compact label="" attachments={form.attachments} setAttachments={v => set('attachments', v)} /></div>
              <Label className="text-xs mt-3 block">Attested By (Name)</Label>
              <Input value={form.attestation_name} onChange={e => set('attestation_name', e.target.value)} placeholder="Auditor's full name" />
            </Section>
          </div>
          <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-white border-t border-slate-200 flex flex-wrap items-center justify-end gap-2 z-10">
            {autoSavedAt && (
              <span className="mr-auto text-xs text-slate-400 flex items-center gap-1" data-testid="autosave-indicator">
                <Save className="w-3.5 h-3.5" /> Auto-saved {autoSavedAt.toLocaleTimeString('en-GB', { timeZone: 'Africa/Lagos', hour: '2-digit', minute: '2-digit', hour12: true })}
              </span>
            )}
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button variant="outline" onClick={() => handleSave('draft')} disabled={saving} className="gap-2" data-testid="save-draft-btn">
              <FileText className="w-4 h-4" /> Save as Draft
            </Button>
            <Button onClick={() => handleSave('submitted')} disabled={saving} className="gap-2 bg-indigo-600 hover:bg-indigo-700" data-testid="submit-audit-report-btn">
              {saving ? <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <Save className="w-4 h-4" />}
              Submit to Management
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <DialogTitle>Audit Report — {viewing?.auditor_name}</DialogTitle>
                <DialogDescription>Read-only view of the submitted farm audit report.</DialogDescription>
              </div>
              <Button size="sm" variant="outline" onClick={downloadPDF} disabled={pdfBusy} className="gap-1 flex-shrink-0" data-testid="download-audit-pdf-btn">
                {pdfBusy ? <div className="animate-spin w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full" /> : <Printer className="w-4 h-4" />}
                PDF
              </Button>
            </div>
          </DialogHeader>
          <div ref={viewRef} className="bg-white p-2">
            {viewing && <AuditView r={viewing} />}
          </div>
        </DialogContent>
      </Dialog>

      {/* Compare last two */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit Comparison — Last Two Visits</DialogTitle>
            <DialogDescription>Side-by-side of the two most recent audits with improvement / decline indicators.</DialogDescription>
          </DialogHeader>
          {reports.length >= 2 && <ComparisonView newer={reports[0]} older={reports[1]} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Row = ({ label, value }) => (
  value === '' || value == null ? null :
    <div className="flex justify-between py-1 border-b border-slate-100 text-sm"><span className="text-slate-500">{label}</span><span className="text-slate-800 font-medium text-right">{value}</span></div>
);

const ViewBlock = ({ title, children }) => (
  <div className="border rounded-lg p-3">
    <h4 className="font-semibold text-slate-800 text-sm mb-2">{title}</h4>
    {children}
  </div>
);

const AuditView = ({ r }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-3 text-sm">
      <div><span className="text-slate-500">Audit Date:</span> <span className="font-medium">{formatDate(r.audit_date)}</span></div>
      <div><span className="text-slate-500">Next Audit:</span> <span className="font-medium">{r.next_audit_date ? formatDate(r.next_audit_date) : '—'}</span></div>
    </div>

    <ViewBlock title="Staffing">
      <Row label="Total Staff" value={r.staffing?.total_staff} />
      <Row label="Present" value={r.staffing?.staff_present} />
      <Row label="Rating" value={r.staffing?.staff_rating} />
      <Row label="Notes" value={r.staffing?.notes} />
      <MediaViewer attachments={r.staffing?.attachments} />
      {(r.staffing_areas || []).map((a, i) => (
        <div key={i} className="mt-2"><Row label={a.area || `Area ${i + 1}`} value={`${a.condition}${a.notes ? ' — ' + a.notes : ''}`} /><MediaViewer attachments={a.attachments} /></div>
      ))}
    </ViewBlock>

    <ViewBlock title="Equipment & Cameras">
      {(r.equipment || []).map((eq, i) => (
        <div key={i} className="mt-1"><Row label={eq.name || `Item ${i + 1}`} value={`${eq.type || ''} · ${eq.state} · Working: ${eq.working}${eq.notes ? ' — ' + eq.notes : ''}`} /><MediaViewer attachments={eq.attachments} /></div>
      ))}
      <Row label="Cameras Total" value={r.cameras?.total} />
      <Row label="Cameras Working" value={r.cameras?.working} />
      <Row label="Cameras State" value={r.cameras?.state} />
      <MediaViewer attachments={r.cameras?.attachments} />
    </ViewBlock>

    <ViewBlock title="Cages & Pens">
      {(r.pens || []).map((p, i) => (
        <div key={i}><Row label={p.pen} value={`Cage: ${p.cage_condition} · General: ${p.general_condition}${p.notes ? ' — ' + p.notes : ''}`} /><MediaViewer attachments={p.attachments} /></div>
      ))}
    </ViewBlock>

    <ViewBlock title="Snail Green House">
      <Row label="Condition" value={r.snail_house?.condition} />
      <Row label="Humidity" value={r.snail_house?.humidity} />
      <Row label="Cleanliness" value={r.snail_house?.cleanliness} />
      <Row label="Notes" value={r.snail_house?.notes} />
      <MediaViewer attachments={r.snail_house?.attachments} />
    </ViewBlock>

    <ViewBlock title="Power Systems">
      <Row label="Solar Available" value={r.power_solar?.available} />
      <Row label="Solar State" value={r.power_solar?.state} />
      <Row label="Solar Notes" value={r.power_solar?.notes} />
      <MediaViewer attachments={r.power_solar?.attachments} />
      <Row label="Generator Available" value={r.power_generator?.available} />
      <Row label="Generator State" value={r.power_generator?.state} />
      <Row label="Fuel Level" value={r.power_generator?.fuel_level} />
      <MediaViewer attachments={r.power_generator?.attachments} />
    </ViewBlock>

    <ViewBlock title="Livestock Census">
      <p className="text-xs font-semibold text-slate-500 mb-1">Chickens</p>
      {(r.chicken_census || []).filter(c => c.count !== '').map((c, i) => <Row key={i} label={c.location} value={`${c.count}${c.notes ? ' — ' + c.notes : ''}`} />)}
      <p className="text-xs font-semibold text-slate-500 mb-1 mt-2">Snails</p>
      {(r.snail_census || []).filter(c => c.count !== '').map((c, i) => <Row key={i} label={c.location} value={`${c.count}${c.notes ? ' — ' + c.notes : ''}`} />)}
    </ViewBlock>

    {r.general_conditions && <ViewBlock title="General Conditions"><p className="text-sm text-slate-700 whitespace-pre-wrap">{r.general_conditions}</p></ViewBlock>}
    {r.recommendations && <ViewBlock title="Recommendations"><p className="text-sm text-slate-700 whitespace-pre-wrap">{r.recommendations}</p></ViewBlock>}
    {r.attachments?.length > 0 && <ViewBlock title="Additional Media"><MediaViewer attachments={r.attachments} /></ViewBlock>}
    {r.attestation_name && <p className="text-sm text-slate-500">Attested by: <span className="font-medium text-slate-800">{r.attestation_name}</span></p>}
  </div>
);

const RANK = { Excellent: 5, Good: 4, Fair: 3, Poor: 2, Critical: 1, Yes: 1, No: 0, Adequate: 4 };

const Trend = ({ newer, older }) => {
  const a = RANK[newer], b = RANK[older];
  if (a == null || b == null || a === b) return <Minus className="w-4 h-4 text-slate-400" />;
  return a > b
    ? <ArrowUp className="w-4 h-4 text-emerald-600" />
    : <ArrowDown className="w-4 h-4 text-red-600" />;
};

const CountTrend = ({ newer, older }) => {
  const a = Number(newer) || 0, b = Number(older) || 0;
  const delta = a - b;
  if (delta === 0) return <span className="text-slate-400 text-xs flex items-center gap-1"><Minus className="w-3 h-3" /> 0</span>;
  return delta > 0
    ? <span className="text-emerald-600 text-xs flex items-center gap-1"><ArrowUp className="w-3 h-3" /> +{delta}</span>
    : <span className="text-red-600 text-xs flex items-center gap-1"><ArrowDown className="w-3 h-3" /> {delta}</span>;
};

const CompareRow = ({ label, newer, older, trend = true, count = false }) => (
  <div className="grid grid-cols-12 items-center gap-2 py-2 border-b border-slate-100 text-sm">
    <div className="col-span-4 text-slate-500">{label}</div>
    <div className="col-span-3 font-medium text-slate-800">{newer ?? '—'}</div>
    <div className="col-span-3 text-slate-500">{older ?? '—'}</div>
    <div className="col-span-2 flex justify-center">
      {count ? <CountTrend newer={newer} older={older} /> : (trend ? <Trend newer={newer} older={older} /> : null)}
    </div>
  </div>
);

const sumCount = (list) => (list || []).reduce((s, c) => s + (Number(c.count) || 0), 0);

const ComparisonView = ({ newer, older }) => {
  const penMap = (r) => Object.fromEntries((r.pens || []).map(p => [p.pen, p]));
  const nPens = penMap(newer), oPens = penMap(older);
  const allPens = Array.from(new Set([...Object.keys(nPens), ...Object.keys(oPens)]));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wide pb-1 border-b">
        <div className="col-span-4">Metric</div>
        <div className="col-span-3">Latest ({formatDate(newer.audit_date)})</div>
        <div className="col-span-3">Previous ({formatDate(older.audit_date)})</div>
        <div className="col-span-2 text-center">Trend</div>
      </div>

      <div>
        <h4 className="font-semibold text-slate-700 text-sm mb-1">Staffing</h4>
        <CompareRow label="Overall Rating" newer={newer.staffing?.staff_rating} older={older.staffing?.staff_rating} />
        <CompareRow label="Staff Present" newer={newer.staffing?.staff_present} older={older.staffing?.staff_present} count />
      </div>

      <div>
        <h4 className="font-semibold text-slate-700 text-sm mb-1">Cages & Pens (General Condition)</h4>
        {allPens.map(pen => (
          <CompareRow key={pen} label={pen} newer={nPens[pen]?.general_condition} older={oPens[pen]?.general_condition} />
        ))}
      </div>

      <div>
        <h4 className="font-semibold text-slate-700 text-sm mb-1">Snail Green House</h4>
        <CompareRow label="Condition" newer={newer.snail_house?.condition} older={older.snail_house?.condition} />
        <CompareRow label="Cleanliness" newer={newer.snail_house?.cleanliness} older={older.snail_house?.cleanliness} />
      </div>

      <div>
        <h4 className="font-semibold text-slate-700 text-sm mb-1">Power & Security</h4>
        <CompareRow label="Solar State" newer={newer.power_solar?.state} older={older.power_solar?.state} />
        <CompareRow label="Generator State" newer={newer.power_generator?.state} older={older.power_generator?.state} />
        <CompareRow label="Cameras Working" newer={newer.cameras?.working} older={older.cameras?.working} count />
      </div>

      <div>
        <h4 className="font-semibold text-slate-700 text-sm mb-1">Livestock Census</h4>
        <CompareRow label="Total Chickens" newer={sumCount(newer.chicken_census)} older={sumCount(older.chicken_census)} count />
        <CompareRow label="Total Snails" newer={sumCount(newer.snail_census)} older={sumCount(older.snail_census)} count />
      </div>

      <div className="flex items-center gap-4 text-xs text-slate-500 pt-2">
        <span className="flex items-center gap-1"><ArrowUp className="w-3.5 h-3.5 text-emerald-600" /> Improved</span>
        <span className="flex items-center gap-1"><ArrowDown className="w-3.5 h-3.5 text-red-600" /> Declined</span>
        <span className="flex items-center gap-1"><Minus className="w-3.5 h-3.5 text-slate-400" /> No change</span>
      </div>
    </div>
  );
};

export default AuditorReport;